import React, { useEffect, useState, useRef, useReducer } from 'react';
import authService from './api-authorization/AuthorizeService'
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Stack from 'react-bootstrap/Stack';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import InputGroup from 'react-bootstrap/InputGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import Tab from 'react-bootstrap/Tab';
import AssetScenarioPlanningModel from './AssetScenarioPlanningModel';
import AssetScenarioPlanningCharts from './AssetScenarioPlanningCharts';
import AssetScenarioPlanningScenarios from './AssetScenarioPlanningScenarios';

const AssetScenarioPlanning = (param) => {

    const [loading, setLoading] = useState(true);
    const [asset, setAsset] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [validated, setValidated] = useState(false);
    const recurringRef = useRef(null);
    const [editVals, setEditVals] = useState([]);
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const [scenario, setScenario] = useState(null);
    const [baseScenario, setBaseScenario] = useState(null);
    const [activeKey, setActiveKey] = useState('model');

    useEffect(() => {
        async function getTable() {
            return populateTable([]);
        }

        async function getInitialScenarios() {
            let data = await Promise.all([
                populateRequirements(),
                populateRecurringCosts(),
                populateScenarios(), 
                populateConditionAssessment()
            ]);

            param.asset.scenarioRequirements = data[0];
            param.asset.scenarioRecurringCosts = data[1];
            param.asset.scenarios = data[2];
            param.asset.lastConditionAssessment = data[3] || new Date().toISOString();
            param.asset.scenarios.forEach(scenario => {
                scenario.scenarioYears = populateTable(scenario.scenarioYears);
                scenario.state = 1;
            });

            var baseScenario = await populateBaselineScenario();
            setBaseScenario(baseScenario);

            param.asset.scenarios.unshift(baseScenario);

            chooseScenario(param.asset.scenarioID);

            setAsset(param.asset);

            setLoading(false);
        }

        async function populateBaselineScenario() {
            let scenario = {
                id: 0,
                name: 'Baseline - No Investment',
                funding: 0,
                state: 1,    // Unmodified
                assetID: param.asset.id,
            };

            let data = await getTable();

            scenario.scenarioYears = data;

            return scenario;
        }

        async function populateRequirements() {
            const token = await authService.getAccessToken();
            const response = await fetch('api/assets/scenarioRequirements?id=' + param.asset.id, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            return data;
        }

        async function populateRecurringCosts() {
            const token = await authService.getAccessToken();
            const response = await fetch('api/assets/recurringcosts?id=' + param.asset.id, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            return data;
        }

        async function populateScenarios() {
            const token = await authService.getAccessToken();
            const response = await fetch('api/assets/scenarios?id=' + param.asset.id, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            return data;
        }

        async function populateConditionAssessment() {

            const token = await authService.getAccessToken();
            const response = await fetch(`api/assets/deteriorationByYear?id=${param.asset.id}`, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            if (response.status === 200) {
       
                const data = await response.json();
                return data.assessmentDate;
            } else {
                return null;
            }
        }

        if (loading) {
            getInitialScenarios();
        };
    }, [loading, param]);

    function populateTable(scenarioYears) {
        const d = new Date();
        let year = d.getFullYear();
        let accumCost = 0;
        let accumCost3 = 0;
        let accumDet = 0;
        let defaultYear = false;
        let costEscalationPercent = param.asset.costEscalationRatePercent;
        let cost = param.asset.scenarioRecurringCosts;
        let sysReqs = param.asset.scenarioRequirements;
        let deteriorRate =  param.asset.deteriorationRatePercent;
        let assessmentYear = JSON.stringify(param.asset.lastConditionAssessment); 

        let baseYear = assessmentYear.slice(1, 5);
        let currentYear = new Date().getFullYear();
        let yearsSinceAssess = currentYear - baseYear;

        if (yearsSinceAssess > 0) {
            let baseCRV = param.asset.currentCRV * Math.pow(1 - costEscalationPercent, currentYear - baseYear);

            for (let i = 1; i <= yearsSinceAssess; i++) {
                accumDet += baseCRV * deteriorRate;
                baseCRV = baseCRV * (1 - costEscalationPercent);
            }
        }

        if (scenarioYears.length < 1) {
            for (let curYear = year; curYear < year + 10; curYear++) {
                scenarioYears.push({ year: curYear });
            }

            defaultYear = true;
        }

        let req13 = 0;
        let req45 = 0;

        sysReqs.forEach(sysReq => {
            if (sysReq.cost !== null && sysReq.cost !== undefined) {
                if (sysReq.priorityNumber <= 3) {
                    req13 += sysReq.cost;
                } else {
                    req45 += sysReq.cost;
                }
            }
        });

        scenarioYears.forEach((scenarioYear, index) => {
            let scenYear = scenarioYear.year;
            let sysCosts = cost.filter(c => c.nextScheduled === scenYear);
            let sysCosts3 = cost.filter(c => (c.nextScheduled > scenYear) && (c.nextScheduled < (scenYear + 3)));
            let discreteCost = 0;
            let funding = 0;

            if (scenarioYear.deferredRenewalFunding !== null && scenarioYear.deferredRenewalFunding !== undefined) {
                funding = scenarioYear.deferredRenewalFunding;
            }

            let crvd = scenarioYears[0].year;
            if (param.asset.assetCRVRevisionHistories !== null && param.asset.assetCRVRevisionHistories.length > 0) {
                let { [param.asset.assetCRVRevisionHistories.length - 1]: lastCrv } = param.asset.assetCRVRevisionHistories;
                let lastDate = new Date(lastCrv.revisionDate);
                crvd = lastDate.getFullYear();
            }

            let crv = param.asset.currentCRV * Math.pow(1 + costEscalationPercent, scenYear - crvd);

            // escalate previous years accumulated recurring costs by the Cost Escalation Percent
            accumCost += accumCost * costEscalationPercent;

            sysCosts.forEach(sysCost => {
                discreteCost += sysCost.replacementCost; // Already escalated * Math.pow(1 + costEscalationPercent, scenYear - year);
            });
           
            accumCost += discreteCost;

            let discreteCost3 = 0;

            sysCosts3.forEach(sysCost3 => {
                discreteCost3 += sysCost3.replacementCost; // Already escalated * Math.pow(1 + costEscalationPercent, sysCost3.nextScheduled - year);
            });

            accumCost3 = discreteCost3;

            // Calculate pre-deduction requirements first
            const preDeductionRequirements = req13 * Math.pow(1 + costEscalationPercent, scenYear - year);

            // Track allocations for re-allocation detection
            const recurringAlloc = funding * param.asset.recurringCostsPercent;
            const requirementsAlloc = funding * param.asset.drRequirementsPercent;
            let wouldhaveReallocated = null;
            let surplusAmount = 0;
            let hasLeftoverFunding = false;

            // Store pre-deduction values to calculate surplus
            const preDeductionRecurring = accumCost;

            // Check if both categories would zero out (leftover funding scenario)
            if ((preDeductionRecurring - recurringAlloc) <= 0 && (preDeductionRequirements - requirementsAlloc) <= 0) {
                // Both would zero out - there's leftover funding
                hasLeftoverFunding = true;
                accumCost = 0;
                req13 = 0;
            } else if ((preDeductionRecurring - recurringAlloc) <= 0 && (preDeductionRequirements - requirementsAlloc) > 0) {
                // Recurring costs would zero out, requirements gets the surplus
                wouldhaveReallocated = 'requirements';
                surplusAmount = Math.max(0, recurringAlloc - preDeductionRecurring);
                // Apply smart re-allocation: use all funding for requirements
                accumCost = 0;
                req13 = preDeductionRequirements - (recurringAlloc + requirementsAlloc);
            } else if ((preDeductionRequirements - requirementsAlloc) <= 0 && (preDeductionRecurring - recurringAlloc) > 0) {
                // Requirements would zero out, recurring costs get the surplus
                wouldhaveReallocated = 'recurring';
                surplusAmount = Math.max(0, requirementsAlloc - preDeductionRequirements);
                // Apply smart re-allocation: use all funding for recurring costs
                accumCost = preDeductionRecurring - (recurringAlloc + requirementsAlloc);
                req13 = 0;
            } else {
                // Normal case - apply funding normally
                accumCost -= funding * param.asset.recurringCostsPercent;
                req13 = preDeductionRequirements - funding * param.asset.drRequirementsPercent;
            }

            if (accumCost < 0) {
                accumCost = 0;
            }

            if (index > 0) {
                accumDet += crv * param.asset.deteriorationRatePercent;
            }

            let accumReq13 = req13;

            let accumReq45 = req45 * Math.pow(1 + costEscalationPercent, scenYear - year);

            if (accumReq45 < 0) {
                accumReq45 = 0;
            }

            // Accumulated deterioration + Accumulated requirements (1-3) + Accumulated recurring costs + <3 year pending recurring costs + escalated requirements (4-5)
            let deferred = accumDet + accumReq13 + accumCost + accumCost3 + accumReq45;            
            
            let fci = deferred / crv;

            scenarioYear.discrete = discreteCost;
            scenarioYear.accum = accumCost;
            scenarioYear.accumReq = accumReq13;
            scenarioYear.accumDet = accumDet;
            scenarioYear.deferred = deferred;
            scenarioYear.modDeferred = deferred;
            scenarioYear.crv = crv;
            scenarioYear.fci = fci;
            scenarioYear.reallocatedTo = wouldhaveReallocated;
            scenarioYear.surplusAmount = surplusAmount;
            scenarioYear.recurringAlloc = recurringAlloc;
            scenarioYear.requirementsAlloc = requirementsAlloc;
            scenarioYear.hasLeftoverFunding = hasLeftoverFunding;

            if (defaultYear) {
                scenarioYear.deferredRenewalFunding = 0;
            }
        });

        return scenarioYears;
    }

    const handleShowEdit = () => {

        setEditVals({
            startMonth: asset.fiscalYearStartMonth,
            drRequirements: asset.drRequirementsPercent,
            recurringCosts: asset.recurringCostsPercent,
            deteriorationRate: asset.deteriorationRatePercent,
            costEscalationRate: asset.costEscalationRatePercent
        });

        setShowModal(true);
        setValidated(false);
    }

    const handleSaveChanges = (event) => {
        async function saveAssetData() {
            const token = await authService.getAccessToken();
            await fetch('api/assets/' + asset.id, {
                method: 'PUT',
                headers: !token ? {} : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(asset),
            });
        };

        const form = event.currentTarget;

        event.preventDefault();

        if (form.checkValidity() === false) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        var newAsset = asset;

        newAsset.fiscalYearStartMonth = 1;
        newAsset.drRequirementsPercent = editVals.drRequirements;
        newAsset.recurringCostsPercent = 1 - editVals.drRequirements;
        newAsset.deteriorationRatePercent = editVals.deteriorationRate;
        newAsset.costEscalationRatePercent = editVals.costEscalationRate;

        newAsset.scenarios.forEach(scenario => {
            scenario.scenarioYears = populateTable(scenario.scenarioYears);
        });

        setAsset(newAsset);
        setShowModal(false);
        setValidated(false);

        saveAssetData();

    }

    const updateScenarios = () => {
        asset.scenarios.forEach(scenario => {
            scenario.scenarioYears = populateTable(scenario.scenarioYears);
        });
        forceUpdate();
    }

    const chooseScenario = (i) => {
        if (i === null) { i = 0; }

        async function saveAssetScenarioID() {
            const token = await authService.getAccessToken();
            await fetch('api/assets/' + param.asset.id, {
                method: 'PUT',
                headers: !token ? {} : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(param.asset),
            });
        };

        var foundScenario = param.asset.scenarios.filter(scenario => scenario.id === i); // find in array based on id
        if (!foundScenario || foundScenario.length === 0) { foundScenario = [param.asset.scenarios[0]]; } // default to baseline if not found
        setScenario(foundScenario[0]);

        if (param.asset.scenarioID !== foundScenario[0].id) { // if changing scenario from previous selection,
            param.asset.scenarioID = foundScenario[0].id; // update in param.asset,
            saveAssetScenarioID(); // and save to db
        }
        
    }

    const switchToModel = () => {
        setActiveKey("model");
    }

    return loading ? (
        <p><em>Loading...</em></p>
    ) : (
        <React.Fragment>
            <div className="d-grid gap-2">
                <Card>
                    <Card.Header>
                        Model Variables
                    </Card.Header>
                    <Card.Body>
                        <Container>
                            <Row>
                                <Col xs="auto">
                                    <Table borderless>
                                        <tbody>
                                            <tr>
                                                <th>Facility Type</th>
                                                <td>{asset.facilityUse.name}</td>
                                            </tr>
                                            <tr>
                                                <th>% DR Requirements</th>
                                                <td>{(asset.drRequirementsPercent * 100).toFixed(2) + '%'}</td>
                                                <th>Cost Escalation Rate/Year</th>
                                                <td>{(asset.costEscalationRatePercent * 100).toFixed(2) + '%'}</td>
                                            </tr>
                                            <tr>
                                                <th>% Recurring Costs</th>
                                                <td>{(asset.recurringCostsPercent * 100).toFixed(2) + '%'}</td>
                                                <th>Deterioration Rate/Year</th>
                                                <td>{(asset.deteriorationRatePercent * 100).toFixed(2) + '%'}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col>
                                    <Stack direction="horizontal" gap={0}>
                                        <Button className="ms-auto" onClick={() => {
                                            handleShowEdit();
                                        }}>Edit Variables</Button>
                                    </Stack>
                                </Col>
                            </Row>
                        </Container>
                    </Card.Body>
                </Card>
                <Modal show={showModal} onHide={() => { setShowModal(false) }} backdrop="static">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Variables</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form noValidate validated={validated} onSubmit={handleSaveChanges}>
                            <div className="d-grid gap-3">
                                <Form.Group as={Row} controlId="formDRReqs">
                                    <Form.Label column sm={6}>% DR Requirements</Form.Label>
                                    <Col sm={4}>
                                        <InputGroup>
                                            <Form.Control required type="Text" defaultValue={(editVals.drRequirements * 100).toFixed(2)}
                                                onChange={({ target: { value } }) => {
                                                    editVals.drRequirements = value / 100;
                                                    recurringRef.current.value = ((1 - value / 100.0) * 100.0).toFixed(2);
                                                }}
                                                pattern="^(100(?:\.00?)?|\d?\d(?:\.\d\d?)?)$"
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Col>
                                </Form.Group>
                                <Row>
                                    <Form.Label column sm={6}>% Recurring Costs</Form.Label>
                                    <Col sm={4}>
                                        <InputGroup>
                                            <Form.Control ref={recurringRef} disabled type="Text" defaultValue={(editVals.recurringCosts * 100).toFixed(2)} />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Col>
                                </Row>
                                <Form.Group as={Row} controlId="formDetRate">
                                    <Form.Label column sm={6}>Deterioration Rate/Year</Form.Label>
                                    <Col sm={4}>
                                        <InputGroup>
                                            <Form.Control required type="Text" defaultValue={(editVals.deteriorationRate * 100).toFixed(2)}
                                                onChange={({ target: { value } }) => editVals.deteriorationRate = value / 100}
                                                pattern="^(100(?:\.00?)?|\d?\d(?:\.\d\d?)?)$"
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="formCostRate">
                                    <Form.Label column sm={6}>Cost Escalation Rate/Year</Form.Label>
                                    <Col sm={4}>
                                        <InputGroup>
                                                <Form.Control disabled type="Text" defaultValue={(editVals.costEscalationRate * 100).toFixed(2)}
                                                onChange={({ target: { value } }) => editVals.costEscalationRate = value / 100}
                                                pattern="^(100(?:\.00?)?|\d?\d(?:\.\d\d?)?)$"
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Col>
                                </Form.Group>
                                <Stack direction="horizontal" gap={1}>
                                    <Button type="submit" >
                                        Save Changes
                                    </Button>
                                    <Button className="ms-auto" variant="link" onClick={() => { setShowModal(false) }} >
                                        Cancel
                                    </Button>
                                </Stack>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal>
                <Tab.Container id="scenario-tabs" defaultActiveKey="model" activeKey={activeKey}>
                    <Stack direction="horizontal">
                        <ToggleButtonGroup name="tabs" className="mb-2">
                            <ToggleButton id="tb1" type="radio" variant={activeKey === "model" ? 'primary' : 'outline-primary'} checked={activeKey === "model"} onClick={() => setActiveKey("model")} >
                                Investment Model
                            </ToggleButton>
                            <ToggleButton id="tb2" type="radio" variant={activeKey === "scenarios" ? 'primary' : 'outline-primary'} checked={activeKey === "scenarios"} onClick={() => setActiveKey("scenarios")} >
                                Scenarios
                            </ToggleButton>
                            <ToggleButton id="tb3" type="radio" variant={activeKey === "charts" ? 'primary' : 'outline-primary'} checked={activeKey === "charts"} onClick={() => setActiveKey("charts")} >
                                Charts
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                    <Tab.Content>
                            <Tab.Pane eventKey="model">
                                <Card>
                                    <Card.Header>{scenario.name}</Card.Header>
                                    <AssetScenarioPlanningModel scenario={scenario} />
                                </Card>

                                {scenario.id !== 0 ? (
                                    <Card className="mt-3">
                                        <Card.Header>vs. Baseline - No Investment</Card.Header>
                                        <AssetScenarioPlanningModel scenario={baseScenario} />
                                    </Card>
                                ) : ''}

                        </Tab.Pane>
                        <Tab.Pane eventKey="scenarios">
                                <AssetScenarioPlanningScenarios asset={asset} updateScenarios={updateScenarios} chooseScenario={chooseScenario} scenario={scenario} switchTab={switchToModel} />
                        </Tab.Pane>
                        <Tab.Pane eventKey="charts">
                            <AssetScenarioPlanningCharts asset={asset} scenario={scenario} />
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </div>
        </React.Fragment>
    );
}

export default AssetScenarioPlanning;